import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

const JobEditModal = ({ open, job, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    // Required fields
    title: '',
    candidateTypeId: '',
    practiceType: '',
    city: '',
    state: '',
    // Optional fields
    salaryType: '',
    salaryMin: '',
    salaryMax: '',
    about: '',
    aboutRaw: '',
    specialtyIds: [],
    subspecialtyIds: [],
    visaIds: [],
    loanForgiveness: '',
    pslfCompatible: '',
    area: '',
    academicType: '',
    shiftType: '',
    link: ''
  })



  useEffect(() => {
    if (job) {
      setFormData({
        title: job.title || '',
        candidateTypeId: job.candidateTypeId || '',
        practiceType: job.practiceType || '',
        city: job.city || '',
        state: job.state || '',
        salaryType: job.salaryType || '',
        salaryMin: job.salaryMin || '',
        salaryMax: job.salaryMax || '',
        about: job.about || '',
        aboutRaw: job.aboutRaw || job.about || '',
        specialtyIds: job.specialtyIds || [],
        subspecialtyIds: job.subspecialtyIds || [],
        visaIds: job.visaIds || [],
        loanForgiveness: job.loanForgiveness || '',
        pslfCompatible: job.pslfCompatible || '',
        area: job.area || '',
        academicType: job.academicType || '',
        shiftType: job.shiftType || '',
        link: job.link || ''
      })
    }
  }, [job])

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }))
  }



  const handleSave = () => {
    const updatedJob = {
      ...job,
      ...formData,
      salaryMin: formData.salaryMin ? Number(formData.salaryMin) : null,
      salaryMax: formData.salaryMax ? Number(formData.salaryMax) : null,
      candidateTypeId: formData.candidateTypeId ? Number(formData.candidateTypeId) : null
    }
    onSave(updatedJob)
  }

  const handleClose = () => {
    onClose()
  }

  if (!job) return null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Edit Job Posting
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Required Fields */}
          <Grid item xs={12}>
            <TextField
              label="Job Title *"
              value={formData.title}
              onChange={handleInputChange('title')}
              fullWidth
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Candidate Type *</InputLabel>
              <Select
                value={formData.candidateTypeId}
                onChange={handleInputChange('candidateTypeId')}
                label="Candidate Type *"
              >
                <MenuItem value={1}>Physician</MenuItem>
                <MenuItem value={2}>Nurse Practitioner</MenuItem>
                <MenuItem value={3}>Physician Assistant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Practice Type *</InputLabel>
              <Select
                value={formData.practiceType}
                onChange={handleInputChange('practiceType')}
                label="Practice Type *"
              >
                <MenuItem value="FullTime">Full Time</MenuItem>
                <MenuItem value="PartTime">Part Time</MenuItem>
                <MenuItem value="Contract">Contract</MenuItem>
                <MenuItem value="Locum">Locum</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label="City *"
              value={formData.city}
              onChange={handleInputChange('city')}
              fullWidth
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label="State *"
              value={formData.state}
              onChange={handleInputChange('state')}
              fullWidth
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Salary Type</InputLabel>
              <Select
                value={formData.salaryType}
                onChange={handleInputChange('salaryType')}
                label="Salary Type"
              >
                <MenuItem value="Annual">Annual</MenuItem>
                <MenuItem value="Hourly">Hourly</MenuItem>
                <MenuItem value="Daily">Daily</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              label="Min Salary"
              type="number"
              value={formData.salaryMin}
              onChange={handleInputChange('salaryMin')}
              fullWidth
              InputProps={{
                startAdornment: '$'
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              label="Max Salary"
              type="number"
              value={formData.salaryMax}
              onChange={handleInputChange('salaryMax')}
              fullWidth
              InputProps={{
                startAdornment: '$'
              }}
            />
          </Grid>
          
          {/* Optional Fields */}
          <Grid item xs={12}>
            <TextField
              label="Job Description"
              value={formData.about}
              onChange={handleInputChange('about')}
              fullWidth
              multiline
              rows={4}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Loan Forgiveness</InputLabel>
              <Select
                value={formData.loanForgiveness}
                onChange={handleInputChange('loanForgiveness')}
                label="Loan Forgiveness"
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>PSLF Compatible</InputLabel>
              <Select
                value={formData.pslfCompatible}
                onChange={handleInputChange('pslfCompatible')}
                label="PSLF Compatible"
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Area</InputLabel>
              <Select
                value={formData.area}
                onChange={handleInputChange('area')}
                label="Area"
              >
                <MenuItem value="Urban">Urban</MenuItem>
                <MenuItem value="Suburban">Suburban</MenuItem>
                <MenuItem value="Rural">Rural</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Academic Type</InputLabel>
              <Select
                value={formData.academicType}
                onChange={handleInputChange('academicType')}
                label="Academic Type"
              >
                <MenuItem value="Academic">Academic</MenuItem>
                <MenuItem value="Community">Community</MenuItem>
                <MenuItem value="Private Practice">Private Practice</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Specialties */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Specialties</InputLabel>
              <Select
                multiple
                value={formData.specialtyIds}
                onChange={handleInputChange('specialtyIds')}
                label="Specialties"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={`Specialty ${value}`} size="small" />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value={1}>Internal Medicine</MenuItem>
                <MenuItem value={2}>Emergency Medicine</MenuItem>
                <MenuItem value={3}>Family Medicine</MenuItem>
                <MenuItem value={4}>Cardiology</MenuItem>
                <MenuItem value={5}>Pediatrics</MenuItem>
                <MenuItem value={6}>Surgery</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Subspecialties */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Subspecialties</InputLabel>
              <Select
                multiple
                value={formData.subspecialtyIds}
                onChange={handleInputChange('subspecialtyIds')}
                label="Subspecialties"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={`Subspecialty ${value}`} size="small" />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value={1}>Interventional Cardiology</MenuItem>
                <MenuItem value={2}>Critical Care</MenuItem>
                <MenuItem value={3}>Gastroenterology</MenuItem>
                <MenuItem value={4}>Oncology</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Visa Sponsorship */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Visa Sponsorship
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.visaIds.includes(1)}
                    onChange={(e) => {
                      const newVisaIds = e.target.checked 
                        ? [...formData.visaIds, 1]
                        : formData.visaIds.filter(id => id !== 1)
                      handleInputChange('visaIds')({ target: { value: newVisaIds } })
                    }}
                  />
                }
                label="H1B"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.visaIds.includes(2)}
                    onChange={(e) => {
                      const newVisaIds = e.target.checked 
                        ? [...formData.visaIds, 2]
                        : formData.visaIds.filter(id => id !== 2)
                      handleInputChange('visaIds')({ target: { value: newVisaIds } })
                    }}
                  />
                }
                label="J1"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.visaIds.includes(3)}
                    onChange={(e) => {
                      const newVisaIds = e.target.checked 
                        ? [...formData.visaIds, 3]
                        : formData.visaIds.filter(id => id !== 3)
                      handleInputChange('visaIds')({ target: { value: newVisaIds } })
                    }}
                  />
                }
                label="O-1"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.visaIds.includes(4)}
                    onChange={(e) => {
                      const newVisaIds = e.target.checked 
                        ? [...formData.visaIds, 4]
                        : formData.visaIds.filter(id => id !== 4)
                      handleInputChange('visaIds')({ target: { value: newVisaIds } })
                    }}
                  />
                }
                label="TN"
              />
            </FormGroup>
          </Grid>
          
          {/* Shift Type (for Nurse Practitioners) */}
          {formData.candidateTypeId === 2 && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Shift Type</InputLabel>
                <Select
                  value={formData.shiftType}
                  onChange={handleInputChange('shiftType')}
                  label="Shift Type"
                >
                  <MenuItem value="Day">Day</MenuItem>
                  <MenuItem value="Night">Night</MenuItem>
                  <MenuItem value="Rotating">Rotating</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          
          {/* External Link */}
          <Grid item xs={12}>
            <TextField
              label="External Link"
              value={formData.link}
              onChange={handleInputChange('link')}
              fullWidth
              placeholder="https://example.com/job-posting"
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default JobEditModal
